/**
 * Top HUD. Stat-row layout follows the Opal reference (icon-free label + value),
 * which stays readable at phone width without becoming a second navigation bar.
 */

import { fateDebtProgress, type GameSession } from '@lastroll/engine';

export interface HudProps {
  session: GameSession;
  muted: boolean;
  onToggleMute: () => void;
  onSave: () => void;
  onQuit: () => void;
}

export function Hud({ session, muted, onToggleMute, onSave, onQuit }: HudProps): JSX.Element {
  const { player, dice } = session.state;
  const hpRatio = player.maxHp > 0 ? player.hp / player.maxHp : 0;
  const debt = fateDebtProgress(dice);

  return (
    <header className="hud">
      <span className="hud__title">THE LAST ROLL</span>

      <div className="hud__stats">
        <div className={`stat stat--hp${hpRatio <= 0.3 ? ' is-low' : ''}`}>
          <div className="stat__label">생명</div>
          <div className="stat__value">
            {player.hp}
            <span className="muted">/{player.maxHp}</span>
          </div>
          <div className="meter">
            <div
              className="meter__fill"
              style={{
                width: `${Math.round(hpRatio * 100)}%`,
                background: hpRatio <= 0.3 ? 'var(--peril)' : 'var(--fortune)',
              }}
            />
          </div>
        </div>

        <div className="stat">
          <div className="stat__label">레벨</div>
          <div className="stat__value">{player.level}</div>
        </div>

        <div className="stat stat--debt" title="운명을 고쳐 쓸수록 쌓인다. 임계치에서 대가가 돌아온다.">
          <div className="stat__label">운명 빚</div>
          <div className="stat__value">{dice.fateDebt}</div>
          <div className="meter">
            <div
              className="meter__fill"
              style={{ width: `${Math.round(debt.ratio * 100)}%`, background: 'var(--debt)' }}
            />
          </div>
        </div>

        {dice.bank.length > 0 && (
          <div className="stat" title="보관한 주사위 눈">
            <div className="stat__label">보관</div>
            <div className="stat__value">{dice.bank.join(' ')}</div>
          </div>
        )}
      </div>

      {/* 폰에서는 제목 옆 첫 줄에 남고, 능력치가 둘째 줄 전체를 쓴다 — 예전엔 세 줄로 넘쳐 화면 1/4 을 먹었다. */}
      <div className="hud__actions">
        <button type="button" className="iconbtn" onClick={onToggleMute} aria-label={muted ? '소리 켜기' : '소리 끄기'}>
          {muted ? '🔇' : '🔊'}
        </button>
        <button type="button" className="iconbtn" onClick={onSave} aria-label="저장">
          💾
        </button>
        <button type="button" className="iconbtn" onClick={onQuit} aria-label="모험 종료">
          ✕
        </button>
      </div>
    </header>
  );
}
